import { createContext, useContext } from "react";
import type { ComposerModeOption } from "./types";

export type ComposerMenu = "attach" | "skills" | "mode" | null;

export interface ComposerContextValue {
  /** Current text value of the composer. */
  value: string;
  setValue: (value: string) => void;
  hasText: boolean;
  /** Whether submit is currently allowed (has text, or empty submit allowed). */
  canSubmit: boolean;
  disabled: boolean;
  /** True while a response is streaming/submitting — send shows Stop. */
  busy: boolean;
  /** Response-mode catalog (app-provided). */
  modes: ComposerModeOption[];
  /** Active mode id. */
  mode: string | undefined;
  setMode: (id: string) => void;
  /** Which single popover/menu is open. */
  openMenu: ComposerMenu;
  setOpenMenu: (menu: ComposerMenu) => void;
  /** Submit the current value. */
  submit: () => void;
}

export const ComposerContext = createContext<ComposerContextValue | null>(null);

/**
 * Read the composer context. Throws when a composer part is rendered outside
 * a `<Composer>` root.
 */
export const useComposer = (): ComposerContextValue => {
  const context = useContext(ComposerContext);
  if (!context) {
    throw new Error("Composer.* components must be used within <Composer>");
  }
  return context;
};

/** The active mode option, or undefined when none matches. */
export const useActiveMode = (): ComposerModeOption | undefined => {
  const { modes, mode } = useComposer();
  return modes.find((option) => option.id === mode);
};

/**
 * The active mode's position along the fast → deep ramp as a fraction (0..1).
 * Falls back to the midpoint when no mode matches. Shared by the glow and the
 * mode-button dot so their colors stay in sync.
 */
export const useModeFraction = (): number => {
  const { modes, mode } = useComposer();
  const index = modes.findIndex((option) => option.id === mode);
  return modes.length > 1 && index >= 0 ? index / (modes.length - 1) : 0.5;
};
